export interface IntegrationStepDTO {
  id: string;
  transcriptMetadataTypeId: number;
  transcriptType: string;
  step: string;
  source: string | null;
}

export class ITPIntegrationStepSdk {
  private baseURL: string;

  /**
   * Constructor
   * @param {string} baseURL - The base URL for the service
   */
  constructor(baseURL: string) {
    if (!baseURL || baseURL.trim() === '') {
      throw new Error('baseURL is required and cannot be empty');
    }

    try {
      new URL(baseURL);
    } catch (error) {
      throw new Error('baseURL must be a valid URL');
    }

    this.baseURL = baseURL;
  }

  /**
   * Wrapper for handling fetch requests and error handling
   * @param {string} endpoint - API endpoint
   * @param {RequestInit} [options] - Request options
   * @returns {Promise<Response>} - Response or throws an error
   */
  private async fetchWrapper(endpoint: string, options?: RequestInit): Promise<Response> {
    const url = `${this.baseURL}${endpoint}`;
    const response = await fetch(url, options);

    if (!response.ok) {
      let errorMessage = `HTTP error! status: ${response.status}`;
      try {
        const errorData = await response.json();
        if (errorData.message) {
          errorMessage += `, message: ${errorData.message}`;
        }
      } catch (e) {
        // ignore
      }
      throw new Error(errorMessage);
    }

    return response;
  }

  /**
   * Get all Integration Steps
   * @param {string} accessToken - user's access token
   * @returns {Promise<IntegrationStepDTO[]>} - List of integration steps
   */
  async getAll(accessToken: string): Promise<IntegrationStepDTO[]> {
    const endpoint = `/itp/integration/steps`;

    const response = await this.fetchWrapper(endpoint, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${accessToken}`
      }
    });

    return response.json();
  }

  /**
   * Get Integration Step by ID
   * @param {string} accessToken - user's access token
   * @param {string} id - Integration Step ID
   * @returns {Promise<IntegrationStepDTO>} - Integration step
   */
  async getById(accessToken: string, id: string): Promise<IntegrationStepDTO> {
    const endpoint = `/itp/integration/steps/${id}`;

    const response = await this.fetchWrapper(endpoint, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${accessToken}`
      }
    });

    return response.json();
  }
}
